// pluginState.js
// Plugin state management following ProseMirror best practices

import {createCitationDecorations, findReferenceMarkRanges} from './decorations';
import {
    calculateCitationNumbers,
    getCitationNumbersForMark,
    getReferenceCountForMark,
    hashCitationState,
} from './citationNumbering';
import {getStoreCitationData} from './storeAdapter';
import {referenceCitationPluginKey} from './index';

/**
 * Resolve citation lookups from the store, falling back to document mark order
 * when the store has references but has not assigned numbers yet (mark order
 * not synced). Without this, `[n]` widgets stay empty until the next keystroke
 * triggers marginMarkersPlugin → updateReferenceOrder.
 *
 * @param {Node} doc
 * @param {Schema} schema
 * @param {string} reportId
 * @returns {{getCitationNumbers: Function, getReferenceCount: Function, citationHash: string}}
 */
export function resolveCitationLookups(doc, schema, reportId) {
    const {numbersByLocalId: storeNumbers, referencesByMark} = getStoreCitationData(reportId);
    const citationHash = hashCitationState(storeNumbers, referencesByMark);

    let numbersByLocalId = storeNumbers;
    const refsNeedNumbers = [...referencesByMark.keys()].some(
        (uid) => getCitationNumbersForMark(uid, referencesByMark, storeNumbers).length === 0
    );

    if (refsNeedNumbers) {
        const ranges = findReferenceMarkRanges(doc, schema);
        const orderedMarks = ranges.map(({uid, from}) => ({uid, from}));
        numbersByLocalId = calculateCitationNumbers(orderedMarks, referencesByMark);
        if (numbersByLocalId.size === 0 && referencesByMark.size > 0) {
            numbersByLocalId = calculateCitationNumbers(
                [...referencesByMark.keys()].map((uid) => ({uid, from: 0})),
                referencesByMark
            );
        }
    }

    return {
        citationHash,
        getCitationNumbers: (uid) =>
            getCitationNumbersForMark(uid, referencesByMark, numbersByLocalId),
        getReferenceCount: (uid) =>
            getReferenceCountForMark(uid, referencesByMark),
    };
}

/**
 * Initialize plugin state
 * @param {EditorState} state - ProseMirror editor state
 * @param {string} reportId - Report ID
 * @returns {Object} Initial plugin state
 */
export function initPluginState(state, reportId) {
    const {getCitationNumbers, getReferenceCount, citationHash} =
        resolveCitationLookups(state.doc, state.schema, reportId);

    return {
        decorations: createCitationDecorations(state.doc, state.schema, getCitationNumbers, getReferenceCount),
        citationHash,
    };
}

/**
 * Apply transaction to plugin state.
 * Pure reducer: depends only on tr and previous plugin state.
 * External store changes reach here via {invalidate: true} meta dispatched
 * from the plugin view's Zustand subscription (see pluginView.js), or via
 * a store-hash mismatch when that dispatch was dropped (nested view.update).
 * @param {Transaction} tr - ProseMirror transaction
 * @param {Object} pluginState - Current plugin state
 * @param {EditorState} newState - New editor state
 * @param {string} reportId - Report ID
 * @returns {Object} New plugin state
 */
export function applyToPluginState(tr, pluginState, newState, reportId) {
    // Invalidation dispatched from plugin.view when external (store) state changes
    const invalidateMeta = tr.getMeta(referenceCitationPluginKey);
    const invalidated = !!(invalidateMeta && invalidateMeta.invalidate);

    const {numbersByLocalId, referencesByMark} = getStoreCitationData(reportId);
    const citationHash = hashCitationState(numbersByLocalId, referencesByMark);
    const storeChanged = citationHash !== pluginState.citationHash;

    const shouldRecompute = tr.docChanged || invalidated || storeChanged;

    if (shouldRecompute) {
        const lookups = resolveCitationLookups(newState.doc, newState.schema, reportId);

        return {
            decorations: createCitationDecorations(
                newState.doc,
                newState.schema,
                lookups.getCitationNumbers,
                lookups.getReferenceCount
            ),
            citationHash: lookups.citationHash,
        };
    }

    // No changes needed, just map existing decorations
    return {
        decorations: pluginState.decorations.map(tr.mapping, tr.doc),
        citationHash: pluginState.citationHash,
    };
}
